import type { ChangeEvent } from 'react'

import { OfMassMessagesService, type TeamMemberDto } from '@api'
import {
  attach,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'

import { $creatorId } from '@/models/creators.ts'
import { toast } from '@/models/toasts.ts'
import { massMessagesService } from '@/modules/chatting/service/mass-message.ts'
import type { MassMessage } from '@/modules/chatting/types.ts'
import { handleError } from '@/utils/error'
import { debounce } from '@/utils/helpers.ts'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy.ts'

export const resetNormalMassMessages = createEvent('reset normal mass message')

export const changeQueryRaw = createEvent<ChangeEvent<HTMLInputElement>>(
  'change normal mass message stats query',
)
const resetEndDate = createEvent('reset normal mass message stats end date')
const resetHasMore = createEvent('reset normal mass message stats has more')
const resetPage = createEvent('reset normal mass message stats page')
const resetStats = createEvent('reset normal mass message stats data')
const resetQuery = createEvent('reset normal mass message stats query')

export const changeQuery = debounce(changeQueryRaw, 500)
export const $query = createStore('', {
  name: 'normal mass message stats query',
})
  .on(changeQueryRaw, (_, event) => event.target.value)
  .reset(resetQuery)

export const $page = createStore(0, {
  name: 'normal mass message stats page',
}).reset(resetPage)

export const $endDate = createStore<string | null>(null, {
  name: 'normal mass message stats end date',
}).reset(resetEndDate)

export const fetchNormalMassMessagesPageFx = attach({
  name: 'normal mass messages fetch page',
  source: {
    creatorId: $creatorId,
    page: $page,
    query: $query,
    endDate: $endDate,
  },
  mapParams: (_: void, { creatorId, page, query, endDate }) => ({
    creatorId: creatorId!,
    page,
    query,
    endDate,
  }),
  effect: createEffect(massMessagesService.getMassMessagesStatistics),
})

fetchNormalMassMessagesPageFx.fail.watch(({ error, params }) => {
  handleError(error, 'Failed to fetch normal mass messages page', () =>
    fetchNormalMassMessagesPageFx(params),
  )
})

export const unsendMessageFx = attach({
  name: "normal mass message's unsend",
  source: {
    creatorId: $creatorId,
  },
  mapParams: (massMessageId: number, { creatorId }) => ({
    creatorId: creatorId!,
    massMessageId,
  }),
  effect: createEffect(OfMassMessagesService.deleteMassMessage),
})

unsendMessageFx.done.watch(() => {
  toast.success(
    'Successfully ran unsent mass message! It will be unsent in the background.',
  )
})

unsendMessageFx.failData.watch((error) => {
  handleError(error, 'Something went wrong while unsending mass message')
})

export const reassignMemberFx = attach({
  name: "normal mass message's reassign member",
  source: {
    creatorId: $creatorId,
  },
  mapParams: (
    params: { userId: TeamMemberDto['userId']; massMessage: MassMessage },
    { creatorId },
  ) => ({
    creatorId: creatorId!,
    ...params,
  }),
  effect: createEffect(
    async ({
      creatorId,
      userId,
      massMessage,
    }: {
      creatorId: number
      userId: TeamMemberDto['userId']
      massMessage: MassMessage
    }) => {
      await massMessagesService.reassignMember(
        creatorId,
        userId,
        massMessage.id,
      )

      return { ...massMessage, userId }
    },
  ),
})

reassignMemberFx.done.watch(() => {
  toast.success('Successfully reassigned the sender!')
})

reassignMemberFx.failData.watch((error) => {
  handleError(
    error,
    'Unexpected error occurred while reassigning sender, please try again later',
  )
})

export const $stats = createStore<MassMessage[]>([], {
  name: 'normal mass message stats',
})
  .on(fetchNormalMassMessagesPageFx.doneData, (store, { list }) =>
    list ? [...store, ...list.filter(isTruthyAndNotEmpty)] : store,
  )
  .on(reassignMemberFx.doneData, (store, message) =>
    store.map((m) => (m.id === message.id ? message : m)),
  )
  .reset(resetStats)

export const $hasMore = createStore(false, {
  name: 'normal mass message stats has more',
})
  .on(fetchNormalMassMessagesPageFx.doneData, (_, { hasMore }) => hasMore)
  .reset(resetHasMore)

$page.on(fetchNormalMassMessagesPageFx.doneData, (page) => page + 1)

$endDate.on(
  fetchNormalMassMessagesPageFx.doneData,
  (_, { list }) => list[list.length - 1]?.date ?? null,
)

sample({
  clock: unsendMessageFx.doneData,
  target: resetStats,
})

sample({
  clock: resetStats,
  target: [resetEndDate, resetHasMore, resetPage],
})

sample({
  clock: resetNormalMassMessages,
  target: [resetStats, resetQuery],
})

sample({
  clock: $query,
  target: resetStats,
})

sample({
  clock: [$query, resetNormalMassMessages, unsendMessageFx.doneData],
  source: { isLoading: fetchNormalMassMessagesPageFx.pending },
  filter: ({ isLoading }) => !isLoading,
  target: fetchNormalMassMessagesPageFx,
})
